import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import WebsiteStepLinks from '@components/Publisher/Websites/Create/WebsiteStepLinks/WebsiteStepLinks';
import EmailVerification from '@components/Publisher/Websites/Create/WebsiteStepFour/components/EmailVerification/EmailVerification';

import {
  changePublisherWebsiteStatusApi,
  getPublisherWebsiteVerificationOptionsApi,
  verifyPublisherWebsiteApi,
} from '@api/publishers.api';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import DropdownPanel from '@uikit/old/DropdownPanel/DropdownPanel';
import Loader from '@uikit/old/Loader/Loader';
import { Button } from '@uikit/Form/Button/Button';
import { getBlobFromBase64 } from '@utils/createFileFromBytesArray';
import downloadCSV from '@utils/downloadCSV';
import { useGoogleLogin } from '@react-oauth/google';
import * as common_pb from 'respona_api/generated/common_pb';
import {
  PublisherWebsiteStatus,
  PublisherWebsiteVerificationType,
} from 'respona_api/generated/common_pb';
import {
  PublishersWebsiteDetailsType,
  PublisherWebsiteVerificationsReasonType,
} from '@ts/publishers.types';

// @ts-ignore
import googleIcon from 'src/assets/googleIcon.svg';
import { addNotification } from '@redux/actions/notifications.actions';
import { DispatchType } from 'src/store';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';

import './WebsiteStepFour.css';

function WebsiteStepFour({
  website,
  updateWebsite,
}: {
  website: PublishersWebsiteDetailsType;
  updateWebsite: (website: PublishersWebsiteDetailsType) => void;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch<DispatchType>();
  const { websiteId } = useParams();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isVerifyingGoogleConsole, setIsVerifyingGoogleConsole] = useState(false);
  const [isVerifyingDnsRecord, setIsVerifyingDnsRecord] = useState(false);
  const [isVerifyingHtmlFile, setIsVerifyingHtmlFile] = useState(false);
  const [isVerifyingHtmlTag, setIsVerifyingHtmlTag] = useState(false);

  const { data: verificationOptions, isLoading } = useQuery(
    ['website-verification', workspaceId, websiteId],
    () => getPublisherWebsiteVerificationOptionsApi(Number(websiteId), workspaceId),
    {
      enabled: !!workspaceId && !!websiteId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (website != null && website.verified) {
      switch (website.verificationType) {
        case PublisherWebsiteVerificationType.GOOGLE_CONSOLE: {
          return setIsVerifyingGoogleConsole(true);
        }
        case PublisherWebsiteVerificationType.DNS_RECORD: {
          return setIsVerifyingDnsRecord(true);
        }
        case PublisherWebsiteVerificationType.HTML_FILE: {
          return setIsVerifyingHtmlFile(true);
        }
        case PublisherWebsiteVerificationType.HTML_TAG: {
          return setIsVerifyingHtmlTag(true);
        }
        case PublisherWebsiteVerificationType.EMAIL: {
          return setIsVerifyingEmail(true);
        }
        default:
          break;
      }
    }
  }, [website]);

  const recheckBtn = (
    text: string,
    verificationType: common_pb.PublisherWebsiteVerificationTypeMap[keyof common_pb.PublisherWebsiteVerificationTypeMap],
    setIsVerifying
  ) => {
    return (
      <div className="recheck-btn">
        <span>{text}</span>
        {verificationType && website?.verified !== true && (
          <Button
            colorIcon="#767676"
            leftIcon="refresh"
            iconSize={10}
            data-testid={`website-wizard-recheck-${verificationType}`}
            onClick={(event) => {
              event.stopPropagation();
              verifyWebsite(verificationType, setIsVerifying);
            }}
          >
            Recheck Status
          </Button>
        )}
      </div>
    );
  };

  const googleAuth = useGoogleLogin({
    onSuccess: ({ code }) =>
      verifyPublisherWebsiteApi(
        Number(websiteId),
        PublisherWebsiteVerificationType.GOOGLE_CONSOLE,
        code
      ).then((response: PublisherWebsiteVerificationsReasonType) => {
        handleResponse(response);
      }),
    flow: 'auth-code',
    scope: 'profile https://www.googleapis.com/auth/webmasters.readonly',
  });

  if (isLoading) {
    return <Loader isFullScreenLoading />;
  }

  const [_, dnsRecord, htmlFile, htmlTag] = verificationOptions.optionsList;

  const downloadHtmlFile = () => {
    const fileBlob = getBlobFromBase64(htmlFile.file.fileBytes, htmlFile.file.format);
    downloadCSV(htmlFile.file.title, fileBlob);
  };

  function handleResponse(response: PublisherWebsiteVerificationsReasonType) {
    if (response.success) {
      dispatch(addNotification({ title: 'Verification succeeded', type: 'success' }));
      updateWebsite({ ...website, verified: true });
      redirectUserTo(history, `${urls.WEBSITES(workspaceId)}`);
    } else {
      dispatch(addNotification({ title: response.reason, type: 'error' }));
    }
  }

  function verifyWebsite(
    verificationType: common_pb.PublisherWebsiteVerificationTypeMap[keyof common_pb.PublisherWebsiteVerificationTypeMap],
    setIsVerifying
  ) {
    setIsVerifying(true);
    verifyPublisherWebsiteApi(Number(websiteId), verificationType).then(
      (response: PublisherWebsiteVerificationsReasonType) => {
        handleResponse(response);
        setIsVerifying(false);
      }
    );
  }

  const headerIcon = (
    isVerifying: boolean,
    type: common_pb.PublisherWebsiteVerificationTypeMap[keyof common_pb.PublisherWebsiteVerificationTypeMap]
  ) => {
    if (website?.verificationType === type && website.verified === true) {
      return 'checkCircled';
    }
    if (isVerifying) {
      return 'spinnerDots';
    }
    return 'crossDelete';
  };

  const headerIconColor = (
    isVerifying: boolean,
    type: common_pb.PublisherWebsiteVerificationTypeMap[keyof common_pb.PublisherWebsiteVerificationTypeMap]
  ) => {
    if (website?.verificationType === type && website.verified === true) {
      return '#23E771';
    }
    if (isVerifying) {
      return '#767676';
    }
    return '#767676';
  };

  if (!website) {
    return <Loader isFullScreenLoading />;
  }

  const onContinueClick = () => {
    changePublisherWebsiteStatusApi(websiteId, workspaceId, PublisherWebsiteStatus.PW_ACTIVE)
      .then(() => {
        redirectUserTo(history, `${urls.WEBSITES(workspaceId)}?refetch=true`);
      })
      .catch((error) => {
        console.error(error);
        dispatch(addNotification({ title: 'Website status update failed', type: 'error' }));
      });
  };

  return (
    <div className="website-step" data-testid="website-wizard-step-4">
      <WebsiteStepLinks
        id={website.id}
        isDisabledStep={false}
        step={4}
        isVerified={website.verified}
      />
      <div className="website-step-container step-4">
        <h2>Verify ownership</h2>
        <p>Receive up to 2X the number of orders by verifying your website.</p>

        <DropdownPanel
          initiallyOpen
          iconColor={headerIconColor(isVerifyingEmail, PublisherWebsiteVerificationType.EMAIL)}
          headerIcon={headerIcon(isVerifyingEmail, PublisherWebsiteVerificationType.EMAIL)}
          classNames={{
            header: 'advanced-settings-wrapper__header',
            content: 'advanced-settings-wrapper__content',
          }}
          headerText={recheckBtn('Email verification', null, setIsVerifyingEmail)}
        >
          <div className="dropdown-text">
            <EmailVerification
              domain={website.domain}
              websiteId={Number(websiteId)}
              handleResponse={handleResponse}
            />
          </div>
        </DropdownPanel>
        <DropdownPanel
          initiallyOpen={false}
          iconColor={headerIconColor(
            isVerifyingGoogleConsole,
            PublisherWebsiteVerificationType.GOOGLE_CONSOLE
          )}
          headerIcon={headerIcon(
            isVerifyingGoogleConsole,
            PublisherWebsiteVerificationType.GOOGLE_CONSOLE
          )}
          classNames={{
            header: 'advanced-settings-wrapper__header',
            content: 'advanced-settings-wrapper__content',
          }}
          headerText={recheckBtn('Google Search Console', null, setIsVerifyingGoogleConsole)}
        >
          <div className="dropdown-text">
            Connect your Google Account and allow access to your Search Console data. If your
            website is verified in your Google Search Console, it will be automatically verified in
            Respona.
            <Button className="login-container__button" onClick={googleAuth}>
              <img src={googleIcon} className="login-container__button-img" alt="google-icon" />
              Continue with Google
            </Button>
          </div>
        </DropdownPanel>
        <DropdownPanel
          initiallyOpen={false}
          iconColor={headerIconColor(
            isVerifyingDnsRecord,
            PublisherWebsiteVerificationType.DNS_RECORD
          )}
          headerIcon={headerIcon(isVerifyingDnsRecord, PublisherWebsiteVerificationType.DNS_RECORD)}
          classNames={{
            header: 'advanced-settings-wrapper__header',
            content: 'advanced-settings-wrapper__content',
          }}
          headerText={recheckBtn(
            'DNS record',
            PublisherWebsiteVerificationType.DNS_RECORD,
            setIsVerifyingDnsRecord
          )}
        >
          <div className="dropdown-text">
            Sign in to your domain name provider (e.g. godaddy.com or namecheap.com) and copy the
            TXT record below to the DNS configuration for respona.com.
          </div>
          <div className="code-block" data-testid="website-wizard-dns-record">
            {dnsRecord.value}
          </div>
          <div className="dropdown-text">
            Note: DNS changes may take some time. If Site Audit doesn’t find the record immediately,
            wait a day before trying again.
          </div>
        </DropdownPanel>
        <DropdownPanel
          initiallyOpen={false}
          iconColor={headerIconColor(
            isVerifyingHtmlFile,
            PublisherWebsiteVerificationType.HTML_FILE
          )}
          headerIcon={headerIcon(isVerifyingHtmlFile, PublisherWebsiteVerificationType.HTML_FILE)}
          classNames={{
            header: 'advanced-settings-wrapper__header',
            content: 'advanced-settings-wrapper__content',
          }}
          headerText={recheckBtn(
            'HTML file',
            PublisherWebsiteVerificationType.HTML_FILE,
            setIsVerifyingHtmlFile
          )}
        >
          <div>
            <div className="dropdown-text">
              1. Upload the HTML verification file below to your domain’s root folder respona.com/.
            </div>
            <div className="dropdown-text">2. Download verification file</div>
            <div className="dropdown-text">
              3. If the upload was successful, the file will be viewable at
            </div>
            <Button
              className="button--download"
              onClick={downloadHtmlFile}
              data-testid="website-wizard-html-file-download"
            >
              Download
            </Button>
          </div>
        </DropdownPanel>
        <DropdownPanel
          initiallyOpen={false}
          iconColor={headerIconColor(isVerifyingHtmlTag, PublisherWebsiteVerificationType.HTML_TAG)}
          headerIcon={headerIcon(isVerifyingHtmlTag, PublisherWebsiteVerificationType.HTML_TAG)}
          classNames={{
            header: 'advanced-settings-wrapper__header',
            content: 'advanced-settings-wrapper__content',
          }}
          headerText={recheckBtn(
            'HTML tag',
            PublisherWebsiteVerificationType.HTML_TAG,
            setIsVerifyingHtmlTag
          )}
        >
          <div className="dropdown-text">
            homepage. It should go in the &lt;head&gt; section, before the first &lt;body&gt;
            section.
          </div>
          <div className="code-block" data-testid="website-wizard-html-tag">
            {htmlTag.value}
          </div>
        </DropdownPanel>
        <div className="website-step-container__footer">
          {website?.verified ? (
            <Button onClick={onContinueClick} data-testid="website-wizard-next">
              Continue
            </Button>
          ) : (
            <>
              {/* Only a verified website is added from here; the skip link below adds it unverified. */}
              <Button disabled data-testid="website-wizard-add-verified">
                Add {website.domain}
              </Button>
              <div
                className="website-step-container__footer-text"
                onClick={() => onContinueClick()}
                data-testid="website-wizard-skip-verification"
              >
                Continue without verification (not recommended)
              </div>
            </>
          )}
        </div>
      </div>
      <div className="website-step-container__dammy" />
    </div>
  );
}

export default WebsiteStepFour;
